import { cn } from "@/lib/cn";

/**
 * Pure-SVG abstract chart placeholder. No real data, no library dependency.
 * Designed to look like a Linear/Vercel telemetry sparkline.
 */
export const ChartPlaceholder = ({
  className,
  height = 180,
  variant = "line", // "line" | "bars"
  testId,
}) => {
  const points = [12, 18, 14, 22, 19, 28, 26, 34, 31, 42, 38, 50];
  const width = 600;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const stepX = width / (points.length - 1);
  const norm = (v) =>
    height - 24 - ((v - min) / Math.max(1, max - min)) * (height - 48);

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${i * stepX} ${norm(p)}`)
    .join(" ");
  const areaPath =
    linePath + ` L ${width} ${height} L 0 ${height} Z`;

  return (
    <div
      data-testid={testId || "chart-placeholder"}
      className={cn(
        "relative w-full overflow-hidden rounded-xl border border-border bg-card p-6",
        className
      )}
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Sample Series
          </div>
          <div className="mt-1 text-2xl font-medium tabular-nums tracking-tight text-foreground">
            $4.28M
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-full bg-brand" />
          Trailing 12m
        </div>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="haevn-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--brand))" stopOpacity="0.18" />
            <stop offset="100%" stopColor="hsl(var(--brand))" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* gridlines */}
        {[0.25, 0.5, 0.75].map((t) => (
          <line
            key={t}
            x1="0"
            x2={width}
            y1={height * t}
            y2={height * t}
            stroke="hsl(var(--border))"
            strokeDasharray="2 4"
            strokeWidth="1"
          />
        ))}

        {variant === "line" ? (
          <>
            <path d={areaPath} fill="url(#haevn-area)" />
            <path
              d={linePath}
              fill="none"
              stroke="hsl(var(--brand))"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        ) : (
          points.map((p, i) => (
            <rect
              key={i}
              x={i * stepX - 6}
              y={norm(p)}
              width="12"
              height={height - norm(p) - 8}
              rx="2"
              fill="hsl(var(--brand))"
              opacity={0.85}
            />
          ))
        )}
      </svg>
    </div>
  );
};

export default ChartPlaceholder;
